import { PartialNote, showValue } from "@/profitability/missing-value";
import { VarianceLine } from "@/profitability/records/estimate-variance";
import { rowVariance } from "@/profitability/records/estimates";
import { JudgedValue } from "@/profitability/records/judged-cells";
import type { RateRule } from "@/profitability/records/judgement";
import { partialFigure } from "@/profitability/records/objects";
import { useReportPresentation } from "@/profitability/report-presentation";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import { isNumericColumn } from "./table-layout";
import { formatReportValue } from "./utils";

/**
 * A cell's figure, the missing dash when it has none, coloured only where
 * the report judges it (`JudgedValue`), under a rollup that adds up only
 * what is known how many it is missing, and under an actual with an
 * estimate the variance from it, quietly.
 */
export const ValueCell = ({
  row,
  column,
  dataset,
  currency,
  rule,
}: {
  row: ReportRow;
  column: ReportColumn;
  dataset: ReportDataset;
  currency: string;
  /** The dataset's rate against its target (`rateRule`), when it has one. */
  rule?: RateRule;
}) => {
  const classic = useReportPresentation() === "classic";
  const formatted = showValue(
    formatReportValue(row[column.key], column, currency),
    column,
  );
  const partial = partialFigure(dataset, row, column.key);
  if (classic)
    return (
      <>
        {formatted}
        <PartialNote partial={partial} />
      </>
    );
  const estimated = isNumericColumn(column)
    ? rowVariance(dataset.columns, column, row)
    : null;
  return (
    <>
      <JudgedValue row={row} column={column} rule={rule}>
        {formatted}
      </JudgedValue>
      <PartialNote partial={partial} />
      {estimated && (
        <VarianceLine
          variance={estimated.variance}
          column={column}
          currency={currency}
        />
      )}
    </>
  );
};
